/**
 * Security & Compliance Page
 * Manage GDPR compliance, audit logs, and security settings
 */

import { useEffect, useState } from "react";
import styles from "./SecurityPage.module.css";
import { Icon } from "@/components/ui/Icon";
import {
  getProcessingActivities,
  getPrivacyPolicyText,
  type AuditLog,
} from "@/lib/gdpr";
import { auditLogsService } from "@/services/auditLogsService";
import {
  getRoleDisplayName,
  getPermissionDisplayName,
  rolePermissions,
  type UserRole,
} from "@/lib/rbac";

/**
 * Security Page Component
 */
export function SecurityPage(): React.ReactElement {
  const [activeTab, setActiveTab] = useState<
    "roles" | "audit" | "gdpr" | "privacy"
  >("roles");
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [isLoadingAuditLogs, setIsLoadingAuditLogs] = useState(true);

  const processingActivities = getProcessingActivities();

  useEffect(() => {
    const loadAuditLogs = async () => {
      setIsLoadingAuditLogs(true);
      try {
        const logs = await auditLogsService.list(100);
        setAuditLogs(logs);
      } catch {
        setAuditLogs([]);
      } finally {
        setIsLoadingAuditLogs(false);
      }
    };

    void loadAuditLogs();
  }, []);

  const roles: UserRole[] = [
    "admin",
    "manager",
    "technician",
    "accountant",
    "client",
  ];

  const getActionLabel = (action: string): string => {
    const labels: Record<string, string> = {
      LOGIN: "Inicio de sesión",
      LOGOUT: "Cierre de sesión",
      VIEW: "Visualización",
      CREATE: "Creación",
      UPDATE: "Actualización",
      DELETE: "Eliminación",
      EXPORT: "Exportación",
    };
    return labels[action] || action;
  };

  const formatTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1>Seguridad y Cumplimiento</h1>
          <p className={styles.subtitle}>
            Gestión de roles, permisos, auditoría y RGPD
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === "roles" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("roles")}
        >
          <Icon name="users" size={20} />
          Roles y Permisos
        </button>
        <button
          className={`${styles.tab} ${activeTab === "audit" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("audit")}
        >
          <Icon name="clipboard" size={20} />
          Registro de Auditoría
        </button>
        <button
          className={`${styles.tab} ${activeTab === "gdpr" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("gdpr")}
        >
          <Icon name="shield" size={20} />
          Cumplimiento RGPD
        </button>
        <button
          className={`${styles.tab} ${activeTab === "privacy" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("privacy")}
        >
          <Icon name="document" size={20} />
          Política de Privacidad
        </button>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Roles and Permissions Tab */}
        {activeTab === "roles" && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Control de Acceso Basado en Roles (RBAC)</h2>
              <p className={styles.sectionSubtitle}>
                Gestiona los permisos de cada rol de usuario
              </p>
            </div>

            <div className={styles.rolesGrid}>
              {/* Roles List */}
              <div className={styles.rolesList}>
                <h3 className={styles.subsectionTitle}>Roles del Sistema</h3>
                {roles.map((role) => (
                  <button
                    key={role}
                    className={`${styles.roleItem} ${selectedRole === role ? styles.selectedRole : ""}`}
                    onClick={() => setSelectedRole(role)}
                  >
                    <Icon name="users" size={20} />
                    <span>{getRoleDisplayName(role)}</span>
                    <span className={styles.permissionCount}>
                      {rolePermissions[role].length} permisos
                    </span>
                  </button>
                ))}
              </div>

              {/* Permissions Details */}
              <div className={styles.permissionsPanel}>
                {selectedRole ? (
                  <>
                    <div className={styles.permissionHeader}>
                      <h3>{getRoleDisplayName(selectedRole)}</h3>
                      <span className={styles.permissionBadge}>
                        {rolePermissions[selectedRole].length} permisos
                        asignados
                      </span>
                    </div>
                    <div className={styles.permissionsList}>
                      {rolePermissions[selectedRole].map((permission) => (
                        <div key={permission} className={styles.permissionItem}>
                          <Icon name="checkCircle" size={16} />
                          <span>{getPermissionDisplayName(permission)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className={styles.emptyState}>
                    <Icon name="users" size={48} />
                    <p>Selecciona un rol para ver sus permisos</p>
                  </div>
                )}
              </div>
            </div>

            {/* Security Features */}
            <div className={styles.securityFeatures}>
              <h3 className={styles.subsectionTitle}>
                Características de Seguridad Implementadas
              </h3>
              <div className={styles.featuresGrid}>
                <div className={styles.featureCard}>
                  <div className={styles.featureIcon}>
                    <Icon name="shield" size={24} />
                  </div>
                  <h4>Cifrado de Datos</h4>
                  <p>
                    AES-256 para datos en reposo
                    <br />
                    TLS 1.3 para datos en tránsito
                  </p>
                </div>
                <div className={styles.featureCard}>
                  <div className={styles.featureIcon}>
                    <Icon name="checkCircle" size={24} />
                  </div>
                  <h4>Autenticación</h4>
                  <p>
                    Autenticación multifactor (MFA)
                    <br />
                    Tokens JWT con expiración
                  </p>
                </div>
                <div className={styles.featureCard}>
                  <div className={styles.featureIcon}>
                    <Icon name="clipboard" size={24} />
                  </div>
                  <h4>Auditoría</h4>
                  <p>
                    Registro de todas las acciones
                    <br />
                    Trazabilidad completa
                  </p>
                </div>
                <div className={styles.featureCard}>
                  <div className={styles.featureIcon}>
                    <Icon name="users" size={24} />
                  </div>
                  <h4>Control de Acceso</h4>
                  <p>
                    RBAC con permisos granulares
                    <br />
                    Principio de mínimo privilegio
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Audit Log Tab */}
        {activeTab === "audit" && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Registro de Auditoría</h2>
              <p className={styles.sectionSubtitle}>
                Historial completo de acciones del sistema
              </p>
            </div>

            <div className={styles.auditTable}>
              <table>
                <thead>
                  <tr>
                    <th>Fecha y Hora</th>
                    <th>Usuario</th>
                    <th>Acción</th>
                    <th>Recurso</th>
                    <th>IP</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingAuditLogs && (
                    <tr>
                      <td colSpan={6}>Cargando registro de auditoría...</td>
                    </tr>
                  )}
                  {!isLoadingAuditLogs && auditLogs.length === 0 && (
                    <tr>
                      <td colSpan={6}>
                        No hay registros de auditoría disponibles.
                      </td>
                    </tr>
                  )}
                  {!isLoadingAuditLogs &&
                    auditLogs.map((log) => (
                      <tr key={log.id}>
                        <td className={styles.timestamp}>
                          {formatTimestamp(log.timestamp)}
                        </td>
                        <td>
                          <div className={styles.userInfo}>
                            <Icon name="users" size={14} />
                            {log.userName}
                          </div>
                        </td>
                        <td>
                          <span className={styles.actionBadge}>
                            {getActionLabel(log.action)}
                          </span>
                        </td>
                        <td>
                          <code className={styles.resourceCode}>
                            {log.resource}:{log.resourceId}
                          </code>
                        </td>
                        <td className={styles.ipAddress}>{log.ipAddress}</td>
                        <td>
                          {log.success ? (
                            <span className={styles.statusSuccess}>
                              <Icon name="checkCircle" size={14} />
                              Exitoso
                            </span>
                          ) : (
                            <span className={styles.statusError}>
                              <Icon name="xCircle" size={14} />
                              Error
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            <div className={styles.auditInfo}>
              <Icon name="alertTriangle" size={20} />
              <div>
                <strong>Retención de logs:</strong>
                <p>
                  Los registros de auditoría se conservan durante 5 años según
                  requisitos legales y de seguridad.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* GDPR Compliance Tab */}
        {activeTab === "gdpr" && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Cumplimiento RGPD</h2>
              <p className={styles.sectionSubtitle}>
                Actividades de tratamiento de datos personales
              </p>
            </div>

            <div className={styles.processingActivities}>
              {processingActivities.map((activity) => (
                <div key={activity.id} className={styles.activityCard}>
                  <div className={styles.activityHeader}>
                    <h3>{activity.name}</h3>
                    <span className={styles.activityId}>{activity.id}</span>
                  </div>

                  <div className={styles.activityDetails}>
                    <div className={styles.activityRow}>
                      <strong>Finalidad:</strong>
                      <span>{activity.purpose}</span>
                    </div>

                    <div className={styles.activityRow}>
                      <strong>Base legal:</strong>
                      <span className={styles.legalBasisBadge}>
                        {activity.legalBasis === "consent" && "Consentimiento"}
                        {activity.legalBasis === "contract" &&
                          "Ejecución de contrato"}
                        {activity.legalBasis === "legal_obligation" &&
                          "Obligación legal"}
                      </span>
                    </div>

                    <div className={styles.activityRow}>
                      <strong>Categorías de datos:</strong>
                      <div className={styles.dataCategoryTags}>
                        {activity.dataCategories.map((category) => (
                          <span
                            key={category}
                            className={styles.dataCategoryTag}
                          >
                            {category.replace(/_/g, " ")}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className={styles.activityRow}>
                      <strong>Período de conservación:</strong>
                      <span>{activity.retention}</span>
                    </div>

                    {activity.recipients && (
                      <div className={styles.activityRow}>
                        <strong>Destinatarios:</strong>
                        <span>{activity.recipients.join(", ")}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Data Subject Rights */}
            <div className={styles.dataRights}>
              <h3 className={styles.subsectionTitle}>
                Derechos de los Interesados
              </h3>
              <div className={styles.rightsGrid}>
                <div className={styles.rightCard}>
                  <Icon name="eye" size={20} />
                  <h4>Acceso</h4>
                  <p>Derecho a saber qué datos procesamos</p>
                </div>
                <div className={styles.rightCard}>
                  <Icon name="edit" size={20} />
                  <h4>Rectificación</h4>
                  <p>Corregir datos inexactos o incompletos</p>
                </div>
                <div className={styles.rightCard}>
                  <Icon name="trash" size={20} />
                  <h4>Supresión</h4>
                  <p>Derecho al olvido en casos aplicables</p>
                </div>
                <div className={styles.rightCard}>
                  <Icon name="download" size={20} />
                  <h4>Portabilidad</h4>
                  <p>Recibir datos en formato estructurado</p>
                </div>
                <div className={styles.rightCard}>
                  <Icon name="xCircle" size={20} />
                  <h4>Oposición</h4>
                  <p>Oponerse al tratamiento de datos</p>
                </div>
                <div className={styles.rightCard}>
                  <Icon name="shield" size={20} />
                  <h4>Limitación</h4>
                  <p>Restringir el procesamiento</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Privacy Policy Tab */}
        {activeTab === "privacy" && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Política de Privacidad</h2>
              <p className={styles.sectionSubtitle}>
                Información sobre protección de datos personales
              </p>
            </div>

            <div className={styles.privacyPolicy}>
              <pre className={styles.policyText}>{getPrivacyPolicyText()}</pre>
            </div>

            <div className={styles.contactInfo}>
              <h3 className={styles.subsectionTitle}>
                Delegado de Protección de Datos
              </h3>
              <div className={styles.contactCard}>
                <div className={styles.contactRow}>
                  <Icon name="mail" size={20} />
                  <div>
                    <strong>Email:</strong>
                    <p>privacidad@biosam.com</p>
                  </div>
                </div>
                <div className={styles.contactRow}>
                  <Icon name="phone" size={20} />
                  <div>
                    <strong>Teléfono:</strong>
                    <p>+34 912 345 678</p>
                  </div>
                </div>
                <div className={styles.contactRow}>
                  <Icon name="mapPin" size={20} />
                  <div>
                    <strong>Dirección:</strong>
                    <p>Calle Ejemplo 123, 28001 Madrid</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SecurityPage;
